import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Camera, X, SwitchCamera, Flashlight } from 'lucide-react';

/**
 * BarcodeScannerDialog - Camera-based barcode scanner using html5-qrcode
 * Opens a dialog with live camera feed for scanning barcodes.
 * Supports 1D (EAN-13, Code128, UPC, etc.) and 2D (QR) codes.
 */
const BarcodeScannerDialog = ({ open, onClose, onScan }) => {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [activeCameraIdx, setActiveCameraIdx] = useState(0);

  const stopScanner = useCallback(async () => {
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState();
        if (state === 2) { // SCANNING
          await html5QrCodeRef.current.stop();
        }
      } catch (e) {
        // Ignore stop errors
      }
      try {
        html5QrCodeRef.current.clear();
      } catch (e) {
        // Ignore clear errors
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScanner = useCallback(async (cameraIdx) => {
    await stopScanner();

    // Small delay to ensure DOM element is available
    await new Promise(r => setTimeout(r, 300));

    if (!scannerRef.current) return;

    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const devices = await Html5Qrcode.getCameras();
      
      if (!devices || devices.length === 0) {
        setError('No cameras found on this device.');
        return;
      }

      setCameras(devices);
      const idx = cameraIdx < devices.length ? cameraIdx : 0;
      setActiveCameraIdx(idx);

      const scanner = new Html5Qrcode('barcode-scanner-region', { verbose: false });
      html5QrCodeRef.current = scanner;

      await scanner.start(
        devices[idx].id,
        {
          fps: 15,
          qrbox: { width: 280, height: 160 },
          aspectRatio: 1.5,
          disableFlip: false,
        },
        (decodedText) => {
          // Success callback
          onScan(decodedText);
          stopScanner();
        },
        () => {
          // Ignore scan failures (happens every frame until something is decoded)
        }
      );
      setScanning(true);
      setError(null);
    } catch (err) {
      console.error('Scanner start error:', err);
      if (err?.toString().includes('NotAllowedError') || err?.toString().includes('Permission')) {
        setError('Camera permission denied. Please allow camera access in your browser settings.');
      } else {
        setError('Failed to start camera. Make sure no other app is using the camera.');
      }
    }
  }, [onScan, stopScanner]);

  useEffect(() => {
    if (open) {
      startScanner(activeCameraIdx);
    }
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSwitchCamera = () => {
    if (cameras.length < 2) return;
    const nextIdx = (activeCameraIdx + 1) % cameras.length;
    startScanner(nextIdx);
  };

  const handleClose = () => {
    stopScanner();
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden" data-testid="barcode-scanner-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="w-5 h-5 text-cyan-500" />
            Scan Barcode
          </DialogTitle>
          <DialogDescription className="sr-only">
            Point your camera at a barcode to scan it
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 pb-4">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center" data-testid="scanner-error">
              <p className="text-red-600 text-sm mb-3">{error}</p>
              <Button size="sm" variant="outline" onClick={() => startScanner(activeCameraIdx)}>
                Retry
              </Button>
            </div>
          ) : (
            <>
              <div className="relative rounded-lg overflow-hidden bg-black">
                <div
                  id="barcode-scanner-region"
                  ref={scannerRef}
                  className="w-full"
                  style={{ minHeight: '260px' }}
                  data-testid="scanner-viewport"
                />
                {/* Scanning overlay indicator */}
                {scanning && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="border-2 border-cyan-400 rounded-lg w-[280px] h-[160px] relative">
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-cyan-400 rounded-tl-lg" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-cyan-400 rounded-tr-lg" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-cyan-400 rounded-bl-lg" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-cyan-400 rounded-br-lg" />
                      {/* Scan line animation */}
                      <div className="absolute left-2 right-2 h-0.5 bg-cyan-400 animate-pulse" style={{ top: '50%' }} />
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground text-center mt-2">
                Point camera at a barcode. It will scan automatically.
              </p>
            </>
          )}

          {/* Controls */}
          <div className="flex items-center justify-center gap-3 mt-3">
            {cameras.length > 1 && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSwitchCamera}
                data-testid="switch-camera-btn"
              >
                <SwitchCamera className="w-4 h-4 mr-1" />
                Switch Camera
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              onClick={handleClose}
              data-testid="close-scanner-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScannerDialog;
